// @ts-strict-ignore
import * as React from 'react';
import {WritableDraft} from 'immer/dist/types/types-external';

import {CountUsersOnceState, PropertyType, ReportName, ResourceType} from 'common/report/constants';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {DateRangeAttributes, DisplayContext, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FilterOperator} from 'common/widgets/property-filter-menu/models/types';
import {MultiAttribution as MultiAttributionType} from 'common/widgets/multi-attribution-menu/types';
import type {
  BehaviorMetricDisplay,
  BehaviorShowClause,
  FormulaShowClause,
  ShowClauseFormula,
} from 'common/report/insights/util/types';
import {RollingWindowSize} from 'common/util';
import {Segmentation} from 'common/types/reports/query';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {SectionItem} from 'common/components/items-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconType} from 'common/components/svg-icon/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {Translatable} from 'common/i18n';
import {BookmarkDragOperationMetadata, QbDragMetadata} from 'common/report/insights/contexts/drag-drop/types';
import {DraggableEntryProps, DroppableProps} from 'common/components/draggable-list/types';
import type {ReplayPreviewRendererCallback} from 'common/widgets/replay-preview/types';
import {WarehouseMetric} from 'app_api/projects/metrics/types';

import {EventItem} from '../query-block/events-menu/types';
import {IncludeCustomPropertyType, MenuItemType, PropertyCombineMode} from '../query-block/properties-menu/types';
import {InlineCohortSegment, SegmentValue} from '../query-block/segment-menu/types';
import {PropertySectionItem} from '../query-block/properties-menu/item-menu-types';
import {QueryOrigin, SelectorTypeAggregationLabel} from '../types/shared-types';

export type StagingRow = boolean | Partial<Entry>;

export type QueryEntrySectionProps = {
  /** when set placeholders will automatically activate when added */
  autoactivatePlaceholders?: boolean;
  /** If true, entrySection action button will be focused when no entry is selected */
  autofocus?: boolean;
  addEntryButtonLabel?: Translatable;
  addEntryButtonTooltipText?: string;
  addEntryLabel?: Translatable;
  addEntryMenuWidth?: number;
  allowCohortFilter?: boolean;
  /** allow addition of inline behaviors */
  behaviorMode?: boolean;
  breakdownAttributionLabeler?: (segment: string) => Translatable;
  buttonTheme?: ButtonTheme;
  canCustomizeNumericBuckets?: boolean;
  canRenameStep?: boolean;
  canUseBreakdownAttribution?: boolean;
  canUseMultiAttribution?: boolean;
  chartType?: BookmarkTypes.ChartType;
  className?: string;
  containedSection?: boolean;
  countUsersOnceState?: CountUsersOnceState;
  dataGroupId?: string;
  dataGroupIds?: Set<string | null>;
  dataGroupsEnabled?: boolean;
  dateRange?: DateRangeAttributes;
  /** date unit set in chart header which is used for getting rolling window size */
  dateUnit?: RollingWindowSize;
  defaultEntry?: QueryEventEntry;
  /** when set, hides the Add button from the row controls */
  disableAddEntry?: boolean;
  disableAutofocus?: boolean;
  /** when set, hides the Add filter button from the row's actions dropdown */
  disableAddEventFilters?: boolean;
  disableAddEntryButtonTooltipText?: boolean;
  disableAddGroupedEventReason?: string;
  /** when set, disables + button in header with reason in tooltip */
  disableAddEntryReason?: string;
  disableAllEventsReason?: string;
  disableCloneEntry?: boolean;
  disableCollapse?: boolean;
  disableDeleteEntry?: boolean;
  disableFirstTimeEventFilters?: boolean;
  disableMapToLookupTable?: boolean;
  /** when set, hides the profile history option and uses the default latest value */
  disableProfileHistory?: boolean;
  /** hide scdOptions */
  disableScdOptions?: boolean;
  /** when set, shows filter row prefixes for filter row types */
  showFilterPrefixForFilterType?: boolean;
  disableTypecast?: boolean;
  disabled?: boolean;
  disabledItems?: Obj;
  /** Disables certain typecasting options for rows. */
  disabledTypecastTypes?: PropertyType[];
  /** specifies which drag and drop group this section is attached to */
  dragData?: Partial<QbDragMetadata>;
  dragProps?: DraggableEntryProps<QbDragMetadata>;
  dropZoneData?: Partial<QbDragMetadata>;
  dropZoneDataAccepts?: DroppableProps<BookmarkDragOperationMetadata>[`accepts`];
  elref?: string;
  emptyPlaceholder?: Entry;
  enableAllDataGroupsForScdProps?: boolean;
  enableEnhancedListFilters?: boolean;
  enableEventIcon?: boolean;
  /** If true, include Show/Hide overflow control for entries */
  enableHideEntry?: boolean;
  /** If true, entry section header will have a sticky header */
  enableStickyHeader?: boolean;
  enableWorkspaceFilterRestrictions?: boolean;
  entries?: Entry[];
  entryType?: EntryType;
  /** list of event aggregator function choices from primative math types */
  eventAggregatorFunctions?: BookmarkTypes.MATH_TYPE[];
  eventNames?: string[];
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  /** whether to hide dimension properties from properties menus */
  excludeDimensionProperties?: boolean;
  /** metric types to exclude from metrics menu */
  excludedMetricsRowTypes?: BookmarkTypes.MetricType[];
  footerItems?: React.ReactNode;
  forceSectionedUI?: boolean;
  forceNewUI?: boolean;
  groupEventBehavior?: GroupEventBehaviors;
  headerItems?: React.ReactNode;
  hideEditControls?: boolean;
  hideEvents?: boolean;
  /** whether to hide custom properties from properties menus */
  includeCustomProperties?: IncludeCustomPropertyType;
  indentFilters?: boolean;
  inlineCohortsOverride?: Obj;
  isBlockedPeople?: boolean;
  isReferencedMetrics?: boolean;
  /** whether to include lookup table properties in the properties menu */
  excludeLookupTableProperties?: boolean;
  /** For list of objects, this is the parent list segmentation. */
  listProperty?: Segmentation;
  managedDragAndDrop?: boolean;
  /** max days time menu can query into data history */
  maxDataHistoryDays?: number;
  measurementBlock?: React.ReactNode;
  metricLevelDataGroups?: boolean;
  /** allow property aggregation operations */
  metricsMode?: boolean;
  minEntries?: number;
  numExclusionSteps?: number;
  onAbortStage?: (committing: boolean) => void;
  onClick?: () => void;
  onEntryGroupLabelClicked?: () => void;
  onKeyUp?: (ev: React.KeyboardEvent) => void;
  onSavedCustomEvent?: (ev: CustomEvent<{customEvent: CustomEventDefinition}>) => void;
  onTrack?: (ev: CustomEvent) => void;
  onUpdate?: (updates: EntrySectionUpdates) => void;
  onUpdateDataGroupId?: (dataGroupId: string) => void;
  otherAggregationOptions?: Array<PropertySectionItem<string>>;
  otherFilterOptions?: Array<PropertySectionItem<string>>;
  otherGroupByOptions?: Array<PropertySectionItem<string>>;
  plotStyle?: BookmarkTypes.ChartPlotStyle;
  /** as of date for projects that do not have real time ingestion */
  projectAsOfDate?: string | null;
  projectHasCohorts?: boolean;
  projectHasPeople?: boolean;
  projectId?: number;
  projectIsFreePlan?: boolean;
  propertiesMenuItemTypes?: MenuItemType[];
  propertiesMenuPropertyTypes?: PropertyType[];
  propertyCombineMode?: PropertyCombineMode;
  /** list of property aggregator function choices from primative math types */
  propertyAggregatorFunctions?: BookmarkTypes.MATH_TYPE[];
  queryOrigin?: QueryOrigin;
  readonly?: boolean;
  /** Renders custom entry prefixes for nested sections */
  renderPrefix?: (props: {index: number; entry: QuerySuperEntry}) => React.ReactNode;
  /** Renders custom entry suffixes for nested sections */
  renderSuffix?: (props: {index: number; entry: QuerySuperEntry}) => React.ReactNode;
  replayPreviewRenderer?: ReplayPreviewRendererCallback;
  reportName?: ReportName;
  restrictTopEventsOnly?: boolean;
  /** Whether the events can be dragged to rearrange */
  rowDraggable?: boolean;
  /** Whether drops should be disallowed despite being draggable */
  rowDropDisabled?: boolean;
  /** if this is a nested entry-section, provide its index in the parent section */
  rowIndex?: number;
  /** Whether the row's label is toggleable */
  rowIndexToggleable?: boolean;
  rowIndexToggleIcon?: SvgIconType;
  /** if set, override rowLabel */
  rowLabel?: string;
  /** for a group of rows, indent labels and use this prefix */
  rowLabelPrefix?: string;
  rowLabelPrefixIcon?: SvgIconType;
  rowLabelTheme?: RowLabelTheme;
  rowLabelType?: RowLabelType;
  rowLabelTypeForExclusionSteps?: RowLabelType;
  rowLimit?: number;
  /** Default row types */
  rowTypes?: RowTypeOption[];
  /** Used by the old QB to render row actions with the first list item filter. can be removed when old QB is deprecated. */
  rowActions?: React.ReactNode;
  sectionName?: `inline-filter` | `inline-cohort` | `global-filter` | `global-breakdown`;
  sectionTitle?: string;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showBorrowedProperties?: boolean;
  showDefaultMetricSelector?: boolean;
  showListInclusionMenu?: boolean;
  showSessions?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  /** Forces a row to be staged. */
  stagingRow?: StagingRow;
  stepCount?: number;
  styleOverride?: string;
  supportB2bCohorts?: boolean;
  suggestedEventType?: string;
  timeDisplayContext?: DisplayContext;
  trackName?: string;
  /** If specified, adds extra properties to the track event. These will override ones from query-builder */
  trackProperties?: UnknownObj;
  userId?: number;
  workspaceId?: number;
};

export enum SelectorTypeDefinitions {
  Behavior = `behavior`,
  EventLaunch = `event_launch`,
  FlowsEvent = `flows_event`,
  GroupedEvent = `grouped_event`,
  Filter = `filter`,
  Segment = `segment`,
  CohortGroup = `cohort_group`,
  PropertyExpansion = `property_expansion`,
  Metrics = `metrics`,
}

/** TODO(pablo): remove union with ResourceType and flatten this list */
export type SelectorType =
  | BookmarkTypes.TopLevelMetricType.MetricEntry
  | BookmarkTypes.TopLevelMetricType.NewFormulaEntry
  | BookmarkTypes.TopLevelMetricType.NewMetricEntry
  | BookmarkTypes.TopLevelMetricType.Warehouse
  | BookmarkTypes.TopLevelMetricType.Metric
  | BookmarkTypes.MetricType
  | ResourceType
  | SelectorTypeDefinitions;

export type RowTypeComplexOption = {
  value: SelectorType;
  placeholderEntry?: Partial<QuerySuperEntry>;
  includeDateRange?: boolean;
  disabledReason?: string;
};

/** A RowType represents what kind of entries can be added to the list */
export type RowTypeOption = SelectorType | RowTypeComplexOption;

/** TODO(pablo): remove type based helpers once we can remove `event`
 * for representing all value selections in an entry
 */
export function isEventBasedEntry(
  entry: Entry,
): entry is MetricEntry | QueryEventEntry | QueryBehaviorEntry | QueryEventLaunch | FlowsQueryEventEntry {
  if (isMetricEntry(entry)) {
    return [
      BookmarkTypes.MetricType.Simple,
      BookmarkTypes.MetricType.CustomEvent,
      BookmarkTypes.MetricType.Event,
      BookmarkTypes.MetricType.Funnel,
      BookmarkTypes.MetricType.Retention,
      BookmarkTypes.MetricType.RetentionFrequency,
    ].includes(entry.behavior.type);
  }
  const type: SelectorType = entry.type;
  const propertyTypes = [
    ResourceType.Event,
    SelectorTypeDefinitions.Behavior,
    SelectorTypeDefinitions.EventLaunch,
    SelectorTypeDefinitions.FlowsEvent,
  ] as SelectorType[];

  return propertyTypes.includes(type || ResourceType.Event);
}

export function isUserBasedEntry(entry: Entry): entry is QueryUserEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return entry.behavior.type === BookmarkTypes.MetricType.People;
  }
  const propertyTypes = [ResourceType.User] as SelectorType[];
  return propertyTypes.includes(entry.type);
}

export function isPropertySelectorType(type: SelectorType): type is QueryPropertyEntry[`type`] {
  const propertyTypes = [
    SelectorTypeDefinitions.Filter,
    SelectorTypeDefinitions.Segment,
    SelectorTypeDefinitions.PropertyExpansion,
  ] as SelectorType[];
  return propertyTypes.includes(type);
}

export {FiltersOperator} from 'common/types/reports/bookmark';

export const FiltersOperatorDisplayLabels: {[x in BookmarkTypes.FiltersOperator]?: Translatable} = {
  [BookmarkTypes.FiltersOperator.And]: ({t}) => t(`filters.group-operator.and`),
  [BookmarkTypes.FiltersOperator.AndAny]: ({t}) => t(`filters.group-operator.and-any`),
  [BookmarkTypes.FiltersOperator.Or]: ({t}) => t(`filters.group-operator.or`),
  [BookmarkTypes.FiltersOperator.OrAll]: ({t}) => t(`filters.group-operator.or-all`),
  [BookmarkTypes.FiltersOperator.Then]: ({t}) => t(`filters.group-operator.then`),
};

export interface SavedCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  renamed?: string;
  // "value" is the cohort's id
  value: number;
  data_group_id?: string;
  negated?: boolean;
}

export interface SpecialCohortItem extends Omit<SavedCohortItem, `value`> {
  // string for pre-defined cohorts ($all_users)
  value: string;
}

interface InlineCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  value: InlineCohortSegment;
  negated?: boolean;
}
export type CohortItem = SavedCohortItem | InlineCohortItem | SpecialCohortItem;

export function isInlineCohortItem(item: CohortItem): item is InlineCohortItem {
  return item.value && (item as InlineCohortItem).value.cohort !== undefined;
}

export function isSavedCohortItem(item: CohortItem): item is SavedCohortItem {
  return item.value && typeof (item as SavedCohortItem).value === `number`;
}

interface BaseEntry {
  _idx?: string;
  /** Used for first class metrics */
  id?: number;
  name?: string;

  dataGroupId?: string | null;

  /** Set of filter clauses to be applied to the event */
  filters?: PropertyFilter[];

  /** Set of behavioral filters */
  behavioralFilters?: PropertyFilter[];
  behavioralFiltersOperator?: BookmarkTypes.FiltersOperator;

  /** Boolean operator to join filter clauses */
  filtersOperator?: BookmarkTypes.FiltersOperator;

  /** Position of the event if this event specifies exclusion */
  excludeRange?: UnknownObj | {from: number; to: number};

  /** Specifies if exclusion event range will include all collection events */
  excludeAll?: boolean;

  /** Numeric property applied on event */
  property?: PropertySectionItem;

  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: BookmarkTypes.MATH_TYPE;

  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: BookmarkTypes.PER_USER_AGGREGATIONS;

  percentile?: number;

  rolling?: {rollingWindowSize?: number};

  cumulative?: boolean;

  countUsersOnceType?: BookmarkTypes.COUNT_USERS_ONCE_TYPE;

  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;

  /** for funnels and flows */
  forward?: number;
  reverse?: number;

  /** for internal use, stores committed values for an entry in stage mode */
  persistedEntry?: Entry;

  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;

  /** internal non-serializable prefix label for the main entry selector */
  prefixLabel?: string;

  /** when set, this entry will be omitted from results */
  isHidden?: boolean;

  /** if true, expands the entry to display its definition (i.e. metrics in a formula, behavior in a metric) */
  isExpanded?: boolean;

  display?: BehaviorMetricDisplay;
}

// TODO: Rename event -> item or cohort/event/filter/segment
export interface QueryEventEntry extends BaseEntry {
  type: ResourceType.Event;
  event: EventItem;
  dateRange?: DateRangeAttributes;
  /** Custom bucketing for HISTOGRAM aggregationOperator  */
  customBucket?: Bucket;
  multiAttribution?: MultiAttributionType;
}

export interface QueryOtherEntry extends BaseEntry {
  type: ResourceType.Other;
  event: EventItem;
  /** If true, the entry is treated as if the section is fully disabled */
  sectionDisabled?: boolean;
}

export function isEventEntry(entry: Entry): entry is QueryEventEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return (
      entry.behavior.type === BookmarkTypes.MetricType.CustomEvent ||
      entry.behavior.type === BookmarkTypes.MetricType.Event ||
      entry.behavior.type === BookmarkTypes.MetricType.Simple
    );
  }
  return entry.type === ResourceType.Event;
}

export type FormulaItem = ShowClauseFormula & {
  error?: string;
};

export interface QueryFormulaEntry extends BaseEntry {
  _idx?: string;
  display?: BehaviorMetricDisplay;
  event: FormulaItem;
  multiAttribution?: MultiAttributionType;
  type: ResourceType.Formula;
}

export interface QuerySavedMetricEntry extends BaseEntry {
  _idx?: string;
  display?: BehaviorMetricDisplay;
  event: BehaviorShowClause;
  type: BookmarkTypes.MetricType.SavedMetric;
}

export function isFormulaEntry(entry: Entry): entry is QueryFormulaEntry {
  // TODO(CC): Create a new formula entry whose basis is MetricEntry
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry && entry.type === ResourceType.Formula;
}

export function isSavedMetricEntry(entry: Entry): entry is QuerySavedMetricEntry {
  return entry && entry.type === BookmarkTypes.MetricType.SavedMetric;
}

export function isInlineFormulaEntry(entry: Entry): boolean {
  if (entry.type === BookmarkTypes.TopLevelMetricType.NewFormulaEntry) {
    return !entry.id;
  }

  return isFormulaEntry(entry) && !entry.event.id;
}

export interface QueryMultiMetricEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Metrics;
  event: null;
}

export interface FlowsQueryEventEntry extends BaseEntry {
  type: SelectorTypeDefinitions.FlowsEvent;
  event: EventItem;
  funnelStep?: boolean;
}

export interface QueryUserEntry extends BaseEntry {
  type: ResourceType.User;
  event: EventItem;
}

export function isQueryUserEntry(entry: Entry): entry is QueryUserEntry {
  return entry.type === ResourceType.User;
}

export interface QueryCohortEntry extends BaseEntry {
  type: ResourceType.Cohort;
  event: CohortItem;
}

export function isCohortEntry(entry: Entry): entry is QueryCohortEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return entry.behavior.type === BookmarkTypes.MetricType.Cohort;
  }
  return entry.type === ResourceType.Cohort;
}

export interface CohortGroupEntry extends BaseEntry {
  groupingOperator?: BookmarkTypes.FiltersOperator.And | BookmarkTypes.FiltersOperator.Or;
  type: SelectorTypeDefinitions.CohortGroup;
  event: CohortItem;
}

export interface QueryBehaviorEntry extends BaseEntry {
  /** @todo [pablo] query-entry-section does not have great typing and support for `Behavior` entries so we pass it as events in some places */
  type: SelectorTypeDefinitions.Behavior | ResourceType.Event;
  event: EventItem;
  dateRange?: DateRangeAttributes;
  entryFilter?: Partial<PropertyFilter>;
  aggregationOperator?: BookmarkTypes.MATH_TYPE;
  filtersOperator?: BookmarkTypes.FiltersOperator | null;
}

export interface QueryEventLaunch extends BaseEntry {
  type: SelectorTypeDefinitions.EventLaunch;
  event: EventItem;
  dateRange?: DateRangeAttributes;
}

/**
 * An entry which contains multiple child entries.
 *
 * TODO (later): shouldn't extend BaseEntry, but that would require more
 * refactoring of query-entry-section.
 */
export interface QueryGroupedEntry extends BaseEntry {
  type: SelectorTypeDefinitions.GroupedEvent;
  event: QueryEventEntry[];
}

export function isGroupedEntry(entry: Entry): entry is QueryGroupedEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry.type === SelectorTypeDefinitions.GroupedEvent && Array.isArray(entry.event);
}

export interface OperatorOverride {
  value: FilterOperator;
  label?: string;
}

export interface QueryFilterEntry extends BaseEntry {
  clearable?: boolean; // TODO remove once we remove customize segments v1
  filterOnly?: boolean;
  hideListQuantifier?: boolean;
  filtersOperator?: BookmarkTypes.FiltersOperator | null;
  type: SelectorTypeDefinitions.Filter;
  event: Partial<PropertyFilter>;
  quickDelete?: boolean;
  quickRename?: boolean;
  operatorOverrides?: OperatorOverride[] | null;
  suppressTracking?: boolean;
  inheritedFromBoard?: boolean;
  /** If true, the value is disabled and cannot be changed. */
  valueDisabled?: boolean;
  /** A tooltip to show when hovering over the value. */
  valueTooltip?: string;
  undraggable?: boolean;
}

export function isFilterEntry(entry: Entry): entry is QueryFilterEntry {
  return entry && entry.type === SelectorTypeDefinitions.Filter;
}

export interface QuerySegmentEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Segment;
  event: SegmentValue;
  customBucket?: Bucket;
  undraggable?: boolean;
  inheritedFromBoard?: boolean;
}

export function isSegmentEntry(entry: Entry): entry is QuerySegmentEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry.type === SelectorTypeDefinitions.Segment;
}

/** Union of entry types which may contain a customBucket. */
export type QueryBucketableEntry = QueryEventEntry | QuerySegmentEntry;

/**
 * QB-internal representation of Filter and Breakdown entry types.
 *
 * This is distinct from event/user property aggregations as a QueryMetricEntry.
 */
export type QueryPropertyEntry = QueryFilterEntry | QuerySegmentEntry;
export function isPropertyEntry(entry: Entry): entry is QueryPropertyEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return isPropertySelectorType(entry.type);
}

export function isObjectEntry(entry: QuerySuperEntry) {
  return (entry as QueryFilterEntry)?.event?.joinPropertyType === PropertyType.Object;
}

/** A single entry representing one cohort, event, property, etc. */
export type QueryMetricEntry =
  | QueryEventEntry
  | QueryFormulaEntry
  | QuerySavedMetricEntry
  | FlowsQueryEventEntry
  | QueryUserEntry
  | QueryCohortEntry
  | QueryBehaviorEntry
  | QueryEventLaunch
  | QueryOtherEntry;

/** All entry types that can be passed to the query builder. Safe for app code to depend on. */
export type QueryEntry = QueryMetricEntry | QueryGroupedEntry | CohortGroupEntry;

/**
 * Query builder internal: all possible types of query entry. App code shouldn't depend on this.
 */
export type QuerySuperEntry = QueryEntry | QueryMetricEntry | QueryPropertyEntry | QueryGroupedEntry;

export type EntryItem = QuerySuperEntry[`event`];

export enum InsightsCountType {
  UniqueUsers = `Unique Users`,
  Total = `Total`,
  TotalSessions = `Total Sessions`,
  FrequencyPerUser = `Frequency per User`,
  PropertyValues = `Aggregate Property`,
  PropertyValuePerUser = `Aggregate Property per User`,
}

export enum RetentionCountType {
  Unique = `Unique`,
  RetentionRate = `Retention Rate`,
  PropertySum = `total`,
  PropertyAverage = `average`,
}

export enum CalculationTypes {
  Rolling = `rolling`,
  Cumulative = `cumulative`,
}
export interface Calculation {
  calculationType: CalculationTypes | null;
  rollingWindowSize?: number;
}

export type AdvancedMenuValue = Calculation | number;

export type CountType = InsightsCountType | RetentionCountType | FunnelsCountType;

export interface CountBy<AdvancedMenuValueType extends AdvancedMenuValue = AdvancedMenuValue> {
  countType: CountType;
  aggregationOperator?: BookmarkTypes.MATH_TYPE;
  aggregationOperatorPerUser?: BookmarkTypes.PER_USER_AGGREGATIONS;
  percentile?: number;
  property?: PropertySectionItem;
  countUsersOnceType?: BookmarkTypes.COUNT_USERS_ONCE_TYPE;
  advancedMenuValue?: AdvancedMenuValueType;
}

export enum Action {
  Add = `add`,
  AddFilter = `addFilter`,
  ApplyProperty = `applyProperty`,
  Clone = `clone`,
  Drag = `drag`,
  Edit = `edit`,
  Group = `group`,
  Ungroup = `ungroup`,
  EditFilter = `editFilter`,
  Remove = `remove`,
  RemoveFilter = `removeFilter`,
  SaveBehavior = `saveBehavior`,
  SaveMetric = `saveMetric`,
  Select = `select`,
  SelectDateRange = `selectDateRange`,
  EditFlowSteps = `editFlowSteps`,
  ToggleValueComparison = `toggleValueComparison`,
}

export enum RowLabelType {
  None = `none`,
  Alpha = `alpha`,
  Numeric = `numeric`,
}

export enum RowLabelTheme {
  Report = `report`,
  Gray = `gray`,
  SessionReplayLabel = `sessionReplayLabel`,
}

export enum EntryType {
  Event = `event`,
  ExclusionEvent = `exclusion-event`,
}

export const SelectorTypesLabels: {[key in SelectorType]?: Translatable} = {
  // event based types
  [ResourceType.Event]: ({t}) => t(`query-builder.select-event`),
  [SelectorTypeDefinitions.FlowsEvent]: ({t}) => t(`query-builder.select-event`),
  [SelectorTypeDefinitions.Behavior]: ({t}) => t(`query-builder.select-behavior`),
  [SelectorTypeDefinitions.EventLaunch]: ({t}) => t(`query-builder.select-event`),
  [SelectorTypeDefinitions.GroupedEvent]: ({t}) => t(`query-builder.select-event`),
  [ResourceType.Cohort]: ({t}) => t(`query-builder.select-cohort`),
  [ResourceType.User]: ({t}) => t(`query-builder.select-profile`),
};

const EMPTY_EVENT: EventItem = Object.freeze({
  id: null,
  custom: false,
  label: ``,
  value: null,
});

export const EMPTY_EVENT_ENTRY = Object.freeze({
  aggregationOperator: BookmarkTypes.PRIMITIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  percentile: null,
  event: EMPTY_EVENT,
  filters: [],
  filtersOperator: BookmarkTypes.FiltersOperator.And,
  property: null,
  type: ResourceType.Event,
}) as QueryEventEntry;

export const EMPTY_EXCLUSION_EVENT_ENTRY: QueryEventEntry = Object.freeze({
  ...EMPTY_EVENT_ENTRY,
  excludeAll: true,
  excludeRange: {},
});

export type SelectorTypeAggregation = {
  /** specifies if the aggregation acts on a property */
  forProperty?: boolean;

  /** Defines across-users aggregations */
  operator?: BookmarkTypes.MATH_TYPE;

  /** Defines per-user aggregations */
  perUserOperator?: BookmarkTypes.PER_USER_AGGREGATIONS;

  label: SelectorTypeAggregationLabel;
  description?: SelectorTypeAggregationLabel;
  subLabel?: SelectorTypeAggregationLabel;
  shortLabel?: SelectorTypeAggregationLabel;

  /** defines aggregations to be displayed in a nested menu */
  subSections?: SelectorTypeAggregation[];

  /** 2nd level aggregation definitions for a 1st pass aggregation, e.g. distribution on a Total per User by Distribution query */
  subAggregations?: SelectorTypeAggregation[];
};

/** When using Add grouped event, how to add the new row. */
export enum GroupEventBehaviors {
  /** Grouped events are disabled. `Add grouped event` is not allowed. */
  None = `none`,
  /** Adding a grouped event should append it to the current entries list. */
  Append = `append`,
  /** Adding a grouped event should create a new group at the current entry. */
  Nest = `nest`,
}

/** When a row-label-prefix is passed, how to display it and handle interactions. */
/** Identifier for a possibly nested entry. Ex: [0,2] = first entry, third child. */
export type RowAddress = [number] | [number, number];

export enum EntryPart {
  Aggregator = `aggregator`,
  NestedBehaviorFilterValue = `nestedBehaviorFilterValue`,
  StepSelector = `stepSelector`,
  FirstTimeFilters = `firstTimeFilters`,
  BehavioralFrequencyFilters = `behavioralFrequencyFilters`,
  BehavioralLookbackWindow = `behavioralLookbackWindow`,
  CustomBucketing = `customBucketing`,
  MetricName = `metricName`,
  MultiAttribution = `multiAttribution`,
  MultiAttributionBehaviorFilterOperator = `multiAttributionBehaviorFilterOperator`,
  MultiAttributionBehaviorFilterValue = `multiAttributionBehaviorFilterValue`,
  MultiAttributionLookback = `multiAttributionLookback`,
  Filters = `filters`,
  NumericProperty = `numericProperty`,
  SubAggregator = `subAggregator`,
  Calculation = `calculation`,
  CountUsersOnce = `countUsersOnce`,
  FilterValue = `filterValue`,
  EntryFilterValue = `entryFilterValue`,
  Value = `value`,
  /** for list of objects breakdown */
  SubValue = `subValue`,
}

export type SectionOpenEntry = {index: number; part: EntryPart} | EmptyObj;

export type Entry = QuerySuperEntry | MetricEntry | QueryMultiMetricEntry;
// This looks wrong
export type EntryWithDisplay = Entry | FormulaShowClause;

export type EntrySectionUpdates<EntryType = Entry> = {
  entries: EntryType[];
  updatedEntry: EntryType;
  updatedEntryIndex: number;
  queryReason?: QueryReason;
  requery: boolean;
  formulaRefUpdates?: Record<string, string>;
};

export type UpdateStatus = {
  dispatchedUpdate: boolean;
};

export enum DROP_POSITION {
  ABOVE,
  BELOW,
  ON,
  HEADER,
}

export interface MetricEntryMeasurement {
  /** This should be set in the "behavior" section instead for cohort or people/profile behaviors */
  dataGroupId?: string;
  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: BookmarkTypes.MATH_TYPE;
  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: BookmarkTypes.PER_USER_AGGREGATIONS;
  percentile?: number;
  countUsersOnceType?: BookmarkTypes.COUNT_USERS_ONCE_TYPE | null;
  cumulative?: boolean;
  multiAttribution?: MultiAttributionType | null;
  /** Numeric property applied on event */
  property?: PropertySectionItem;
  rolling?: BookmarkTypes.RollingMeasurement;

  // funnel specific
  /** controls conversion step, if it's null it's "all step", other wise it's step {eventIndex} -> step {eventIndex + 1} */
  // TODO: change to funnelStepIndex
  stepIndex?: number | null;

  // retention specific
  retentionBuckets?: Array<SectionItem<number>>;
  retentionBucketIndex?: number;
  retentionCumulative?: boolean;

  // funnel and retention specific
  attributionSelection?: number | string;
}

export interface MetricEntryBehavior {
  type: BookmarkTypes.MetricType;
  // depends on the type, id could be any of the
  // 1. custom-event
  // 2. funnel
  // 3. retention
  // 4. cohort
  id?: number;

  // b2b data group
  // If metric-level data groups are enabled, this is only relevant for people/profile and cohort types. For other
  // types, the effective dataGroupId is on the measurement
  dataGroupId?: string | null;
  // for `event` type, name is the "Event Name"
  // for `profile` it's always "$all_people"
  // for `saved-cohort`, it holds name of the cohort
  // for `inline-cohort`, it holds the name user input
  // for `inline-custom-event`, it holds the name user input
  // for `inline-funnels`, it holds the name user input
  name?: string | null;
  // only appliable for "event" | "saved-cohort" | "profile"
  renamed?: string;

  dataset?: string;
  resourceType?: BookmarkTypes.InsightsResourceType;

  // filters applied on the "behavior block"
  filters?: PropertyFilter[];
  filtersOperator?: BookmarkTypes.FiltersOperator;
  filtersDeterminer?: `all` | `any`;

  //========================
  // generic nestable field
  //========================
  // for inline-custom-event, this holds custom event definition
  // for inline-funnels, this holds funnel steps
  // for inline-retention, this holds exactly 2 entries - born & event
  // special case: for funnels event comparision feature, this will be nested 2 levels
  behaviors?: MetricEntryBehavior[];

  //========================
  // inline-cohort specific
  //========================
  raw_cohort?: TODO; // RawCohort

  //========================
  // insight specific
  //========================
  // only appliable when county type is histogram
  customBucket?: Bucket | null;

  //========================
  // funnels specific
  //========================
  funnelOrder?: BookmarkTypes.FunnelOrder;
  // exclusion could only contain `event` | `saved-custom-event`
  exclusions?: BookmarkTypes.ExclusionFunnelStep[];
  // "holding property constant" feature, selets a property
  aggregateBy?: Segmentation[];
  conversionWindowDuration?: number;
  conversionWindowUnit?: BookmarkTypes.ConversionWindowUnit;
  funnelReentryMode?: BookmarkTypes.FunnelReentryModeType;
  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  //========================
  // retention specific
  //========================
  retentionType?: BookmarkTypes.RetentionType;
  // calendar day
  retentionAlignmentType?: BookmarkTypes.RetentionAlignmentType;
  // day | week | year
  retentionUnit?: TimeUnit;
  // on or after | on Each day | on or before
  retentionUnboundedMode?: BookmarkTypes.RetentionUnboundedModeType;
  // custom retention range
  retentionCustomBucketSizes?: number[];
  disableCohortize?: boolean;

  // for saved behaviors, this marks UI state as out of sync with the saved definition
  hasUnsavedChanges?: boolean;
}

export interface MetricEntry {
  _idx?: string | null | undefined;
  // Only for first class metrics
  id?: number | null;
  name?: string | null;
  userNamed?: boolean;
  type:
    | BookmarkTypes.TopLevelMetricType.MetricEntry
    | BookmarkTypes.TopLevelMetricType.Metric
    | BookmarkTypes.TopLevelMetricType.NewMetricEntry
    | BookmarkTypes.TopLevelMetricType.NewFormulaEntry
    | BookmarkTypes.TopLevelMetricType.Warehouse;

  dataGroupId?: string | null;
  //======================
  // behavior
  //======================
  behavior: MetricEntryBehavior;
  //=======================
  // measurement
  //=======================
  measurement: MetricEntryMeasurement;
  //=======================
  // goals
  //=======================
  goals?: BookmarkTypes.Goal[] | null;
  //========================
  // UI specific fields for metric
  //========================
  // rest of the UI state could just be added as needed.
  // here just list out some of the existing UI state in type def.
  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;
  /** for internal use, stores committed values for an entry in stage mode */
  persistedEntry?: MetricEntry;
  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;
  /** internal non-serializable prefix label for the main entry selector */
  prefixLabel?: string;
  /** if true, expands the metric entry that renders metric behavior section */
  isExpanded?: boolean;
  /** when set, this entry will be omitted from results */
  isHidden?: boolean;
  /**
   * If false, behavior should be synced. That is, when creating a show clause,
   * behavior should be consisted of id and type only.
   */
  hasUnsavedChanges?: boolean | undefined;

  display?: BehaviorMetricDisplay;

  /** Formula Specific Field: defines the formula string (i.e. A /B * 100) */
  definition?: string;
  /** Formula Specific Field: defines what each formula variable refers to (i.e. A = Purchase [Unique]) */
  referencedMetrics?: MetricEntry[];
  /** Formula Specific Field: defines any validation error for the formula */
  error?: string;

  /** SQL Specific Field: the raw SQL query text to execute against a warehouse source */
  query?: string;
  /** SQL Specific Field: id of the warehouse source the query runs against */
  warehouseSourceId?: number | null;
  /** SQL Specific Field: whether the query result is a timeseries or a single numeric value */
  metricType?: `timeseries` | `numeric`;
  /** SQL Specific Field: column used as the time axis for timeseries results */
  timeColumn?: string | null;
  /** SQL Specific Field: column used as the value for results */
  valueColumn?: string | null;
  syncInterval?: WarehouseMetric[`definition`][`syncInterval`];
  aggregation?: WarehouseMetric[`definition`][`aggregation`];
}

export function isMetricEntry(entry: Entry | WritableDraft<Entry>): entry is MetricEntry | WritableDraft<MetricEntry> {
  return (
    entry.type === BookmarkTypes.TopLevelMetricType.NewMetricEntry ||
    entry.type === BookmarkTypes.TopLevelMetricType.MetricEntry ||
    entry.type === BookmarkTypes.TopLevelMetricType.Metric
  );
}

export function isQuerySuperEntry(entry: Entry): entry is QuerySuperEntry {
  return Object.prototype.hasOwnProperty.call(entry, `event`);
}

export function isQueryMultiMetricEntry(entry: Entry): entry is QueryMultiMetricEntry {
  return entry.type === SelectorTypeDefinitions.Metrics;
}

export interface FilteringCapabilities {
  canApplyFilter: boolean;
  canApplyFirstTimeEventFilter: boolean;
  firstTimeFilterDisabled: boolean;
}
